import { useEffect, useRef, type FC } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { MATRIX_ICON_BTN, MATRIX_OUTLINE_BTN } from './matrixStyles';
import type {
  Ics234MatrixItemDraft,
  Ics234MatrixItemEditState,
  Ics234MatrixItemRef,
  Ics234ObjectiveRow,
} from './types';
import { ics234MatrixItemRefsMatch } from './types';

export function displayIcs234MatrixItemName(name: string, fallback: string) {
  const trimmed = name.trim();
  return trimmed || fallback;
}

export function getIcs234MatrixItemDraftName(
  ref: Ics234MatrixItemRef,
  fallback: string,
  editingItem: Ics234MatrixItemEditState | null
) {
  if (
    editingItem &&
    ics234MatrixItemRefsMatch(editingItem.ref, ref) &&
    editingItem.draft.kind === ref.kind
  ) {
    return editingItem.draft.name;
  }
  return fallback;
}

type Ics234MatrixInlineNameFieldProps = {
  name: string;
  editing: boolean;
  placeholder: string;
  readOnly?: boolean;
  onChange: (name: string) => void;
  className?: string;
};

export const Ics234MatrixInlineNameField: FC<
  Ics234MatrixInlineNameFieldProps
> = ({ name, editing, placeholder, readOnly = false, onChange, className }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing || readOnly) return;
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [editing, readOnly]);

  if (editing && !readOnly) {
    return (
      <input
        ref={inputRef}
        autoFocus
        value={name}
        placeholder={placeholder}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          'tw:h-8 tw:w-full tw:min-w-0 tw:rounded-md tw:border tw:border-neutral-500 tw:bg-neutral-900 tw:px-2 tw:text-sm tw:font-medium tw:text-white tw:outline-none tw:placeholder:text-neutral-400',
          className
        )}
      />
    );
  }

  return (
    <span className={cn('tw:text-sm tw:font-medium tw:text-white', className)}>
      {displayIcs234MatrixItemName(name, placeholder)}
    </span>
  );
};

type Ics234MatrixItemEditControlsProps = {
  itemRef: Ics234MatrixItemRef;
  itemLabel: string;
  canMutate: boolean;
  isSaving: boolean;
  editingItem: Ics234MatrixItemEditState | null;
  disabled?: boolean;
  onStartItemEdit: (ref: Ics234MatrixItemRef) => void;
  onCancelItemEdit: () => void;
  onSaveItem: () => void;
  compact?: boolean;
};

export const Ics234MatrixItemEditControls: FC<
  Ics234MatrixItemEditControlsProps
> = ({
  itemRef,
  itemLabel,
  canMutate,
  isSaving,
  editingItem,
  disabled = false,
  onStartItemEdit,
  onCancelItemEdit,
  onSaveItem,
  compact = false,
}) => {
  if (!canMutate || disabled) {
    return null;
  }

  const editing =
    !!editingItem && ics234MatrixItemRefsMatch(editingItem.ref, itemRef);

  if (editing) {
    return (
      // Not a control: the row it sits in toggles on click, so the action
      // buttons need their clicks kept from reaching it.
      <div
        role="presentation"
        className={cn(
          'tw:flex tw:shrink-0 tw:items-center tw:gap-1',
          compact && 'tw:flex-wrap'
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={cn('tw:h-7 tw:gap-1 tw:px-2 tw:text-xs', MATRIX_ICON_BTN)}
          disabled={isSaving}
          onClick={onCancelItemEdit}
        >
          <X className="tw:h-3.5 tw:w-3.5" />
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          className={cn(
            'tw:h-7 tw:gap-1 tw:px-2 tw:text-xs',
            MATRIX_OUTLINE_BTN
          )}
          disabled={isSaving}
          onClick={onSaveItem}
        >
          <Check className="tw:h-3.5 tw:w-3.5" />
          Save
        </Button>
      </div>
    );
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      className={MATRIX_ICON_BTN}
      aria-label={`Edit ${itemLabel}`}
      onClick={(event) => {
        event.stopPropagation();
        onStartItemEdit(itemRef);
      }}
    >
      <Pencil className="tw:h-4 tw:w-4" />
    </Button>
  );
};

export type Ics234WorkAnalysisMatrixProps = {
  objectives: Ics234ObjectiveRow[];
  canEdit: boolean;
  isSaving: boolean;
  editingItem: Ics234MatrixItemEditState | null;
  onStartItemEdit: (ref: Ics234MatrixItemRef) => void;
  onCancelItemEdit: () => void;
  onPatchItemDraft: (draft: Ics234MatrixItemDraft) => void;
  onSaveItem: () => void;
  onAddObjective: () => void;
  onAddStrategy: (objectiveId: string) => void;
  onAddTactic: (objectiveId: string, strategyId: string) => void;
  onDeleteObjective: (objectiveId: string) => void;
  onDeleteStrategy: (objectiveId: string, strategyId: string) => void;
  onDeleteTactic: (
    objectiveId: string,
    strategyId: string,
    tacticId: string
  ) => void;
};
